import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from '@/lib/apiUrl';

const API = `${getApiUrl()}/api`;
const CACHE_KEY = 'nsofar_currency';

const CurrencyContext = createContext({});

export function CurrencyProvider({ children }) {
  const [currencyCode, setCurrencyCode] = useState('USD');
  const [symbol, setSymbol] = useState('$');
  const [effectiveRate, setEffectiveRate] = useState(1.0);
  const [currencyLoaded, setCurrencyLoaded] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [manualOverride, setManualOverride] = useState(false);

  useEffect(() => {
    let manual = false;
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY));
      if (cached && cached.currencyCode) {
        setCurrencyCode(cached.currencyCode);
        setSymbol(cached.symbol);
        setEffectiveRate(cached.effectiveRate);
        setCurrencyLoaded(true);
        manual = !!cached.manual;
        setManualOverride(manual);
      }
    } catch {}

    axios.get(`${API}/currency/rates`)
      .then(res => setCurrencies(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});

    if (manual) return;

    axios.get(`${API}/currency/detect`)
      .then(res => {
        const { currency_code, symbol: sym, effective_rate } = res.data;
        setCurrencyCode(currency_code);
        setSymbol(sym);
        setEffectiveRate(effective_rate);
        setCurrencyLoaded(true);
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          currencyCode: currency_code,
          symbol: sym,
          effectiveRate: effective_rate,
          manual: false,
        }));
      })
      .catch(() => setCurrencyLoaded(true));
  }, []);

  const setCurrency = useCallback((code) => {
    const c = currencies.find(x => x.currency_code === code);
    if (!c) return;
    setCurrencyCode(c.currency_code);
    setSymbol(c.symbol);
    setEffectiveRate(c.effective_rate);
    setManualOverride(true);
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      currencyCode: c.currency_code,
      symbol: c.symbol,
      effectiveRate: c.effective_rate,
      manual: true,
    }));
  }, [currencies]);

  const formatPrice = useCallback((priceUsd) => {
    if (priceUsd === 0) return 'Free';
    const converted = Math.ceil(priceUsd * effectiveRate);
    return `${symbol}${converted.toLocaleString()}`;
  }, [effectiveRate, symbol]);

  return (
    <CurrencyContext.Provider value={{ currencyCode, symbol, effectiveRate, currencyLoaded, formatPrice, currencies, setCurrency, manualOverride }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}
